import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ChevronDown, ChevronRight, Link2 } from 'lucide-react';
import type {
  JobHandle,
  SmartoltConnectionInput,
  SmartoltMigration,
  SmartoltMigrationDecision,
  SmartoltMigrationGroup,
  SmartoltMigrationGroupPlan,
  SmartoltMigrationGroupStatus,
  SmartoltMigrationProbe,
  SmartoltSettings,
} from '@simpleacs/shared';
import {
  parseSmartoltSubdomain,
  smartoltBaseUrlFor,
  SMARTOLT_MIGRATION_GROUP_LABELS,
  SMARTOLT_MIGRATION_ORDER,
} from '@simpleacs/shared';
import { api, ApiRequestError } from '../../lib/api';
import { formatTimestamp } from '../../lib/format';
import { runJob } from '../../lib/jobs';
import { useTasks } from '../../lib/tasks';
import { WizardDialog } from '../../components/WizardDialog';
import {
  Button,
  ErrorNotice,
  Field,
  Input,
  Pill,
  Spinner,
  TBody,
  THead,
  TRow,
  Table,
  type PillTone,
} from '../../components/ui';
import { CollapsibleGroup, useCollapsibleItem } from '../../components/ui/CollapsibleGroup';
import {
  credentialDraftPartial,
  credentialsForApply,
  type OltCredentialDraft,
} from '../../lib/smartolt-credentials';
import { OltCredentialsPanel } from './OltCredentialsPanel';

/**
 * Migrate from SmartOLT.
 *
 * "Give the SmartOLT URL and API key, choose what to bring, and the customer is
 * migrated" (operator, 2026-09-10). Three steps and a tray job:
 *
 *   Connect — which SmartOLT and its read key, checked by a probe that says
 *             what the token can see (one count per group).
 *   Choose  — which groups to bring, every one ticked by default.
 *   Review  — the whole inventory is read ONCE and frozen as one diff per
 *             group; the operator reads it and types each group's write count
 *             back. Apply hands the frozen plans to the tray and closes.
 *
 * Nothing read from SmartOLT is written in the same act, and a field this
 * platform already holds is never overwritten — a conflict is shown, never
 * applied. The dialog never talks to a chassis: an imported ONU is a RECORD,
 * and the next sweep reconciles it with what the OLT holds.
 *
 * Opened with `existing`, it is the same dialog at the Review step over a
 * migration already taken: read-only for the groups that landed, with Apply
 * still offered for the ones that are pending or failed (resumable).
 */

/** Where the tray row and the toast link: the SmartOLT tab, where the list is. */
export const SMARTOLT_MIGRATIONS_HREF = '/settings?tab=smartolt';

export const MIGRATIONS_QUERY_KEY = ['smartolt', 'migrations'] as const;

/**
 * What each group brings, in one sentence — the plan's table
 * (docs/smartolt-api-compatibility.md, "Step 2 — Choose") in the operator's
 * words, so the choice is made from the screen rather than from a document.
 */
const GROUP_BRINGS: Record<SmartoltMigrationGroup, string> = {
  olts:
    'Each OLT’s name, vendor, model and IP. Credentials are not in their API — every OLT lands “needs CLI/SNMP credentials” and is finished in the OLT wizard.',
  vlans: 'Each OLT’s VLAN table, marked by purpose where their naming says so.',
  zones: 'Zone names and parents; geometry where they give it.',
  odbs: 'Splitters (ODBs) under their zones — names, and geometry where they give it.',
  onu_types: 'The ONU type library, merged by name, with capabilities as they report them.',
  speed_profiles: 'Speed profiles — name, rate and direction.',
  onus:
    'ONU records: serial, name, type, placement by zone and ODB, external id, WAN mode, VLANs, speed profiles, GPS. New rows for serials this platform does not hold; existing rows keep every field they have.',
  ports: 'Ethernet and WiFi port modes per ONU, where their record carries them.',
};

const DECISION_TONE: Record<SmartoltMigrationDecision, PillTone> = {
  create: 'success',
  fill: 'primary',
  unchanged: 'neutral',
  conflict: 'warn',
  refused: 'danger',
};

const DECISION_LABEL: Record<SmartoltMigrationDecision, string> = {
  create: 'create',
  fill: 'fill',
  unchanged: 'unchanged',
  conflict: 'conflict — kept as ours',
  refused: 'refused',
};

const DECISIONS: readonly SmartoltMigrationDecision[] = [
  'create',
  'fill',
  'unchanged',
  'conflict',
  'refused',
];

const GROUP_STATUS_TONE: Record<SmartoltMigrationGroupStatus, PillTone> = {
  pending: 'neutral',
  applied: 'success',
  failed: 'danger',
  skipped: 'neutral',
};

/** How many rows one fold renders before it says "and N more". */
const ROWS_SHOWN = 300;

const DEFAULT_ONU_MAX = 5000;

/** The number Apply asks to be typed back: what the group would WRITE. */
export function writeCount(plan: SmartoltMigrationGroupPlan): number {
  return plan.counts.create + plan.counts.fill;
}

/** A group Apply can still run: never applied, or applied and failed. */
export function isApplicable(plan: SmartoltMigrationGroupPlan): boolean {
  return plan.status === 'pending' || plan.status === 'failed';
}

/** Plans in dependency order, whatever order the API stored them in. */
export function orderedPlans(migration: SmartoltMigration): SmartoltMigrationGroupPlan[] {
  const rank = new Map(SMARTOLT_MIGRATION_ORDER.map((g, i) => [g, i] as const));
  return [...migration.plans].sort(
    (a, b) => (rank.get(a.group) ?? 99) - (rank.get(b.group) ?? 99),
  );
}

/**
 * The tray's done sentence: per group, what landed.
 * "OLTs 2 created · Zones 5 created · ONUs 180 created, 12 filled, 3 conflicts".
 */
export function summariseOutcome(
  migration: SmartoltMigration,
  groups: readonly SmartoltMigrationGroup[],
): string {
  const parts: string[] = [];
  for (const plan of orderedPlans(migration)) {
    if (!groups.includes(plan.group)) continue;
    const label = SMARTOLT_MIGRATION_GROUP_LABELS[plan.group];
    if (plan.status === 'failed') {
      parts.push(`${label} failed${plan.outcome ? ` (${plan.outcome})` : ''}`);
      continue;
    }
    if (plan.status === 'skipped') {
      parts.push(`${label} skipped`);
      continue;
    }
    const bits: string[] = [];
    if (plan.counts.create > 0) bits.push(`${plan.counts.create} created`);
    if (plan.counts.fill > 0) bits.push(`${plan.counts.fill} filled`);
    if (plan.counts.conflict > 0)
      bits.push(`${plan.counts.conflict} conflict${plan.counts.conflict === 1 ? '' : 's'}`);
    if (plan.counts.refused > 0) bits.push(`${plan.counts.refused} refused`);
    parts.push(`${label} ${bits.length ? bits.join(', ') : 'nothing to write'}`);
  }
  return parts.length ? parts.join(' · ') : 'Nothing was applied.';
}

const STEPS = [
  { key: 'connect', title: 'Connect' },
  { key: 'choose', title: 'Choose' },
  { key: 'review', title: 'Review' },
] as const;

export function MigrateDialog({
  existing = null,
  onClose,
}: {
  /** Re-open a migration already taken, at its review. */
  existing?: SmartoltMigration | null;
  onClose: () => void;
}) {
  const tasks = useTasks();
  const queryClient = useQueryClient();
  const [step, setStep] = useState(existing ? 2 : 0);

  // --- Connect -----------------------------------------------------------------

  /**
   * The saved connection, when there is one to offer.
   *
   * The settings read is superadmin-only today; anyone else gets a 403, which
   * here simply means "no saved connection to offer" — the typed form is the
   * whole step for them. Never retried: a 403 is an answer, not a hiccup.
   */
  const saved = useQuery({
    queryKey: ['smartolt', 'settings', 'for-migration'],
    queryFn: async () => {
      try {
        return await api.get<SmartoltSettings>('/smartolt/settings');
      } catch (err) {
        if (err instanceof ApiRequestError && (err.status === 403 || err.status === 404)) return null;
        throw err;
      }
    },
    retry: false,
    enabled: !existing,
  });
  const savedConfigured = Boolean(saved.data?.configured);
  const [useSaved, setUseSaved] = useState<boolean | null>(null);
  useEffect(() => {
    if (useSaved === null && saved.isFetched) setUseSaved(savedConfigured);
  }, [useSaved, saved.isFetched, savedConfigured]);
  const usingSaved = useSaved === true && savedConfigured;

  const [where, setWhere] = useState('');
  const [token, setToken] = useState('');
  const [probe, setProbe] = useState<SmartoltMigrationProbe | null>(null);
  const [error, setError] = useState<string | null>(null);

  /**
   * What the typed address means, derived with the SAME function the server
   * uses: a subdomain when it is (or contains) a smartolt.com host, otherwise a
   * full base URL for a self-hosted or proxied instance.
   */
  const typedSubdomain = parseSmartoltSubdomain(where);
  const typedIsUrl = /^https?:\/\/\S+$/i.test(where.trim());
  const typedUrl = typedSubdomain
    ? smartoltBaseUrlFor(typedSubdomain)
    : typedIsUrl
      ? where.trim().replace(/\/+$/, '')
      : null;

  const connection = (): SmartoltConnectionInput | undefined => {
    if (usingSaved) return undefined;
    return typedSubdomain
      ? { subdomain: typedSubdomain, token: token.trim() }
      : { baseUrlOverride: typedUrl ?? where.trim(), token: token.trim() };
  };
  const connectionReady = usingSaved || (typedUrl !== null && token.trim().length > 0);

  const check = useMutation({
    mutationFn: () =>
      api.post<SmartoltMigrationProbe>('/smartolt/migrations/probe', {
        connection: connection(),
      }),
    onSuccess: (r) => {
      setProbe(r);
      setError(r.ok ? null : (r.warnings[0] ?? 'SmartOLT did not answer.'));
    },
    onError: (e) => {
      setProbe(null);
      setError(e instanceof Error ? e.message : 'Could not reach SmartOLT');
    },
  });

  /** Anything that changes the connection invalidates what was checked. */
  const touchConnection = () => {
    setProbe(null);
    setError(null);
  };

  // --- Choose ------------------------------------------------------------------

  const [groups, setGroups] = useState<Set<SmartoltMigrationGroup>>(
    () => new Set(SMARTOLT_MIGRATION_ORDER),
  );
  const [onuMax, setOnuMax] = useState(String(DEFAULT_ONU_MAX));

  /**
   * A group the probe could not count is one the token cannot read; it cannot
   * be chosen. `null` (or absent from a successful probe) is that signal — the
   * warning that names the group is the hint, with a plain sentence when the
   * probe's wording does not name it.
   */
  const unreadable = (group: SmartoltMigrationGroup): string | null => {
    if (!probe?.ok) return null;
    const count = probe.counts[group];
    if (typeof count === 'number') return null;
    const label = SMARTOLT_MIGRATION_GROUP_LABELS[group].toLowerCase();
    const named = probe.warnings.find(
      (w) => w.toLowerCase().includes(label) || w.toLowerCase().includes(group),
    );
    return named ?? 'The token could not read this from SmartOLT.';
  };
  useEffect(() => {
    if (!probe?.ok) return;
    setGroups((current) => {
      const next = new Set(current);
      for (const g of SMARTOLT_MIGRATION_ORDER) if (unreadable(g)) next.delete(g);
      return next;
    });
    // `unreadable` is derived from `probe` alone.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [probe]);

  const toggle = (group: SmartoltMigrationGroup) =>
    setGroups((current) => {
      const next = new Set(current);
      if (next.has(group)) next.delete(group);
      else next.add(group);
      return next;
    });
  const onuMaxNumber = Math.min(20_000, Math.max(1, Number(onuMax) || DEFAULT_ONU_MAX));

  // --- Review ------------------------------------------------------------------

  const [migration, setMigration] = useState<SmartoltMigration | null>(existing);
  const [confirm, setConfirm] = useState<Partial<Record<SmartoltMigrationGroup, string>>>({});
  /** Credentials typed for the OLTs the review asks about, keyed by item key. */
  const [oltCredentials, setOltCredentials] = useState<Record<string, OltCredentialDraft>>({});

  const create = useMutation({
    mutationFn: () =>
      api.post<SmartoltMigration>('/smartolt/migrations', {
        connection: connection(),
        groups: SMARTOLT_MIGRATION_ORDER.filter((g) => groups.has(g)),
        onuMaxRecords: onuMaxNumber,
      }),
    onSuccess: async (m) => {
      setMigration(m);
      setConfirm({});
      setError(null);
      await queryClient.invalidateQueries({ queryKey: MIGRATIONS_QUERY_KEY });
    },
    onError: (e) => setError(e instanceof Error ? e.message : 'Could not read SmartOLT'),
  });

  const discard = useMutation({
    mutationFn: (id: string) => api.delete(`/smartolt/migrations/${encodeURIComponent(id)}`),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: MIGRATIONS_QUERY_KEY });
      onClose();
    },
    onError: (e) => setError(e instanceof Error ? e.message : 'Could not discard'),
  });

  const plans = useMemo(() => (migration ? orderedPlans(migration) : []), [migration]);
  /** The groups Apply would run: every one still pending or failed. */
  const applicable = plans.filter(isApplicable);
  /** The groups that need their write count typed back. */
  const needsCount = applicable.filter((p) => writeCount(p) > 0);
  const allTyped = needsCount.every((p) => Number(confirm[p.group] ?? '') === writeCount(p));
  /**
   * The OLTs the review asks credentials for — only while the OLTs group is
   * still to be applied, because the credentials ride that group's apply.
   */
  const oltsPlan = applicable.find((p) => p.group === 'olts');
  const credentialItems = (oltsPlan?.items ?? []).filter((i) => i.credentials === 'needed' && i.olt);
  /** A half-typed login holds Apply: it would otherwise be dropped without a word. */
  const credentialsHalfTyped = credentialItems.some((i) => credentialDraftPartial(oltCredentials[i.key]));
  const migrationOpen =
    migration !== null && migration.status !== 'expired' && migration.status !== 'discarded';
  const canApply =
    migrationOpen &&
    migration.status !== 'applying' &&
    applicable.length > 0 &&
    allTyped &&
    !credentialsHalfTyped;

  /**
   * Apply hands the frozen plans to the tray and closes — like Resync. The id
   * and the groups are captured first because the dialog is gone by the time
   * the job settles; the summary is built from the migration the job returns.
   */
  const startApply = () => {
    if (!migration) return;
    const id = migration.id;
    const chosen = applicable.map((p) => p.group);
    const confirmCounts = Object.fromEntries(
      applicable.map((p) => [p.group, writeCount(p)]),
    ) as Record<SmartoltMigrationGroup, number>;
    const credentials = credentialsForApply(credentialItems, oltCredentials);
    onClose();
    tasks.start<SmartoltMigration>({
      label: 'Migrate from SmartOLT',
      href: SMARTOLT_MIGRATIONS_HREF,
      // The job answers a per-group outcome list; the migration itself, with
      // its plans updated and each item's detail saying what landed, is
      // re-read once it settles — that is what the tray sentence reads from.
      run: async () => {
        await runJob<unknown>(
          await api.post<JobHandle>(`/smartolt/migrations/${encodeURIComponent(id)}/apply`, {
            groups: chosen,
            confirmCounts,
            ...(Object.keys(credentials).length > 0 ? { oltCredentials: credentials } : {}),
          }),
        );
        return api.get<SmartoltMigration>(`/smartolt/migrations/${encodeURIComponent(id)}`);
      },
      done: (m) => summariseOutcome(m, chosen),
      invalidate: [
        ['olts'],
        ['onus'],
        ['zones'],
        ['splitters'],
        ['onu-types'],
        ['speed-profiles'],
        [...MIGRATIONS_QUERY_KEY],
      ],
    });
  };

  const next = () => {
    setError(null);
    // Entering Review reads the whole inventory, once.
    if (step === 1) create.mutate();
    setStep((s) => Math.min(s + 1, STEPS.length - 1));
  };
  const back = () => {
    setError(null);
    setStep((s) => Math.max(0, s - 1));
  };

  const canAdvance = step === 0 ? probe?.ok === true : step === 1 ? groups.size > 0 : false;

  const applyTitle = !migration
    ? 'Waiting for the review'
    : migration.status === 'expired'
      ? 'This review has expired — take a new one.'
      : migration.status === 'applying'
        ? 'It is being applied.'
        : applicable.length === 0
          ? 'Every group has been applied.'
          : !allTyped
            ? 'Type each group’s number back to apply.'
            : undefined;

  return (
    <WizardDialog
      title="Migrate from SmartOLT"
      subtitle={
        existing
          ? `Review taken ${formatTimestamp(existing.createdAt)} from ${existing.baseUrl}`
          : 'Reads their whole inventory, shows one frozen diff per group, and writes only what you confirm. It never touches a chassis.'
      }
      maxWidth={step === 2 ? '5xl' : 'xl'}
      steps={[...STEPS]}
      currentStep={step}
      onClose={onClose}
      bodyClassName="max-h-[70vh] space-y-4 overflow-y-auto p-5"
      footer={
        <div className="flex items-center justify-between gap-2">
          {/* A review nobody applies is kept as a draft and listed; Discard is
              the deliberate act of dropping it. Before a review exists there
              is nothing to discard, so the left button is a plain Cancel. */}
          {migration && migrationOpen && migration.status !== 'applying' ? (
            <Button
              variant="danger"
              loading={discard.isPending}
              onClick={() => discard.mutate(migration.id)}
            >
              Discard
            </Button>
          ) : (
            <Button variant="ghost" onClick={onClose}>
              Cancel
            </Button>
          )}
          <div className="flex gap-2">
            {step > 0 && !existing && (
              <Button onClick={back} disabled={create.isPending}>
                Back
              </Button>
            )}
            {step < 2 ? (
              <Button variant="primary" disabled={!canAdvance} onClick={next}>
                Next
              </Button>
            ) : (
              <Button
                variant="primary"
                disabled={!canApply}
                onClick={startApply}
                title={applyTitle}
              >
                Apply
              </Button>
            )}
          </div>
        </div>
      }
    >
      {step === 0 && (
        <div className="space-y-4">
          {saved.isLoading ? (
            <p className="flex items-center gap-2 text-sm text-muted">
              <Spinner /> Looking for a saved connection…
            </p>
          ) : savedConfigured && saved.data ? (
            <Field label="Which SmartOLT">
              <div className="grid gap-2 sm:grid-cols-2">
                <ChoiceButton
                  selected={usingSaved}
                  title="The saved connection"
                  detail={saved.data.baseUrl}
                  onSelect={() => {
                    setUseSaved(true);
                    touchConnection();
                  }}
                />
                <ChoiceButton
                  selected={!usingSaved}
                  title="A different one"
                  detail="Type the subdomain and a read API key below."
                  onSelect={() => {
                    setUseSaved(false);
                    touchConnection();
                  }}
                />
              </div>
            </Field>
          ) : null}

          {!usingSaved && (
            <div className="grid gap-4 sm:grid-cols-2">
              <Field
                label="SmartOLT subdomain or URL"
                hint={
                  typedUrl
                    ? `Reads ${typedUrl}`
                    : 'If you sign in at https://acme.smartolt.com, it is “acme”. A full URL is for a self-hosted instance.'
                }
              >
                <Input
                  value={where}
                  onChange={(e) => {
                    setWhere(e.target.value);
                    touchConnection();
                  }}
                  placeholder="acme"
                  className="font-mono"
                  autoFocus
                />
              </Field>
              <Field label="Read API key" hint="Used for this migration; not stored by this dialog.">
                <Input
                  type="password"
                  name="so-migration-token"
                  value={token}
                  onChange={(e) => {
                    setToken(e.target.value);
                    touchConnection();
                  }}
                  className="font-mono"
                  autoComplete="new-password"
                  data-lpignore="true"
                  data-1p-ignore="true"
                  data-form-type="other"
                />
              </Field>
            </div>
          )}

          <div className="flex items-center justify-between gap-3">
            <p className="text-xs text-faint">
              Check reads a count per group and stores nothing. A token that only reads is enough.
            </p>
            <Button
              size="sm"
              icon={<Link2 className="size-3.5" />}
              loading={check.isPending}
              disabled={!connectionReady}
              onClick={() => check.mutate()}
            >
              Check
            </Button>
          </div>

          {probe && <ProbeSummary probe={probe} />}
        </div>
      )}

      {step === 1 && probe && (
        <div className="space-y-3">
          <p className="text-sm text-muted">
            Everything the token can see is ticked. A group left out is not read at all; an item
            whose parent is not brought and not already here is refused at review, not invented.
          </p>
          <div className="grid gap-2">
            {SMARTOLT_MIGRATION_ORDER.map((group) => {
              const blocked = unreadable(group);
              const count = probe.counts[group];
              const checked = groups.has(group) && !blocked;
              return (
                <div key={group} className="grid gap-1">
                <label
                  className={`flex items-start gap-3 rounded-lg border p-3 ${
                    blocked
                      ? 'cursor-not-allowed border-line opacity-60'
                      : checked
                        ? 'cursor-pointer border-primary bg-primary-soft'
                        : 'cursor-pointer border-line hover:bg-surface-2'
                  }`}
                  title={blocked ?? undefined}
                >
                  <input
                    type="checkbox"
                    className="mt-1"
                    checked={checked}
                    disabled={Boolean(blocked)}
                    onChange={() => toggle(group)}
                  />
                  <span className="min-w-0 flex-1">
                    <span className="flex flex-wrap items-center gap-2">
                      <span className="text-sm font-medium text-ink">
                        {SMARTOLT_MIGRATION_GROUP_LABELS[group]}
                      </span>
                      {blocked ? (
                        <Pill tone="warn">cannot read</Pill>
                      ) : (
                        <Pill tone="neutral">{typeof count === 'number' ? count : '—'}</Pill>
                      )}
                    </span>
                    <span className="mt-0.5 block text-xs text-muted">
                      {blocked ?? GROUP_BRINGS[group]}
                    </span>
                  </span>
                </label>
                {/* Outside the label on purpose: a click in the box must not
                    toggle the checkbox it sits under. */}
                {group === 'onus' && checked && (
                  <div className="flex flex-wrap items-center gap-2 pl-8 text-xs text-muted">
                    Read at most
                    <Input
                      type="number"
                      min={1}
                      max={20000}
                      value={onuMax}
                      onChange={(e) => setOnuMax(e.target.value)}
                      className="w-24 text-center font-mono"
                    />
                    ONUs — a review nobody reads is the failure this bound prevents.
                  </div>
                )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {step === 2 && (
        <div className="space-y-4">
          {create.isPending ? (
            <div className="flex flex-col items-center gap-3 py-12 text-center">
              <Spinner className="size-6" />
              <p className="text-sm text-ink">Reading SmartOLT’s whole inventory…</p>
              <p className="max-w-md text-xs text-muted">
                Every OLT, zone, type, profile and ONU the token can see is read once and matched
                against what this platform holds. This can take a while on a large fleet. Nothing
                is written.
              </p>
            </div>
          ) : migration ? (
            <>
              <div className="flex flex-wrap items-center gap-2 text-xs text-muted">
                <Pill state tone={MIGRATION_STATUS_TONE[migration.status]}>{migration.status}</Pill>
                <span className="font-mono">{migration.baseUrl}</span>
                {migration.expiresAt > Date.now() && migration.status === 'draft' && (
                  <span>· worth applying until {formatTimestamp(migration.expiresAt)}</span>
                )}
              </div>
              {migration.warnings.map((w) => (
                <p
                  key={w}
                  className="rounded-lg border border-warn/30 bg-warn-soft px-3 py-2 text-sm text-warn"
                >
                  {w}
                </p>
              ))}
              {migration.status === 'expired' && (
                <ErrorNotice error="This review has expired — the fleet moves, and a stale diff lies. Take a new one." />
              )}
              <p className="text-sm text-muted">
                Conflicts and refusals are <span className="font-medium text-ink">shown, never written</span>:
                a value this platform already holds stays exactly as it is, and an item that cannot
                be brought says why. Only creates and fills land.
              </p>
              <CollapsibleGroup>
                {plans.map((plan) => (
                  <GroupPanel
                    key={plan.group}
                    plan={plan}
                    typed={confirm[plan.group] ?? ''}
                    askCount={migrationOpen && migration.status !== 'applying' && isApplicable(plan) && writeCount(plan) > 0}
                    onType={(v) => setConfirm((c) => ({ ...c, [plan.group]: v }))}
                  />
                ))}
              </CollapsibleGroup>
              {migrationOpen && migration.status !== 'applying' && (
                <OltCredentialsPanel
                  items={credentialItems}
                  drafts={oltCredentials}
                  onChange={(key, draft) => setOltCredentials((c) => ({ ...c, [key]: draft }))}
                />
              )}
            </>
          ) : null}
        </div>
      )}

      {error && <ErrorNotice error={error} />}
    </WizardDialog>
  );
}

const MIGRATION_STATUS_TONE: Record<SmartoltMigration['status'], PillTone> = {
  draft: 'primary',
  applying: 'info',
  applied: 'success',
  partial: 'warn',
  expired: 'neutral',
  discarded: 'neutral',
};

function ChoiceButton({
  selected,
  title,
  detail,
  onSelect,
}: {
  selected: boolean;
  title: string;
  detail: string;
  onSelect: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onSelect}
      className={`rounded-lg border p-3 text-left ${
        selected ? 'border-primary bg-primary-soft' : 'border-line hover:bg-surface-2'
      }`}
    >
      <span className="block text-sm font-medium text-ink">{title}</span>
      <span className="mt-0.5 block truncate font-mono text-xs text-muted" title={detail}>
        {detail}
      </span>
    </button>
  );
}

/** What the token can see, one count per group. */
function ProbeSummary({ probe }: { probe: SmartoltMigrationProbe }) {
  return (
    <div className="rounded-lg border border-line bg-surface-2/40 p-3">
      <div className="mb-2 flex flex-wrap items-center gap-2">
        <Pill state tone={probe.ok ? 'success' : 'danger'} dot>
          {probe.ok ? 'connected' : 'no answer'}
        </Pill>
        <span className="font-mono text-xs text-muted">{probe.baseUrl}</span>
      </div>
      {probe.ok && (
        <div className="grid gap-x-4 gap-y-1 text-xs sm:grid-cols-2">
          {SMARTOLT_MIGRATION_ORDER.map((group) => {
            const count = probe.counts[group];
            return (
              <div key={group} className="flex items-center justify-between gap-2">
                <span className="text-muted">{SMARTOLT_MIGRATION_GROUP_LABELS[group]}</span>
                <span className={typeof count === 'number' ? 'font-mono text-ink' : 'text-warn'}>
                  {typeof count === 'number' ? count : 'cannot read'}
                </span>
              </div>
            );
          })}
        </div>
      )}
      {probe.warnings.map((w) => (
        <p key={w} className="mt-2 text-xs text-warn">
          {w}
        </p>
      ))}
    </div>
  );
}

/**
 * One group's frozen diff: the counts, the typed-back number when it will
 * write, and the items behind a fold — a fleet's ONUs is hundreds of rows and
 * eight open tables is a wall.
 */
function GroupPanel({
  plan,
  typed,
  askCount,
  onType,
}: {
  plan: SmartoltMigrationGroupPlan;
  typed: string;
  askCount: boolean;
  onType: (value: string) => void;
}) {
  // One group's items unfolded at a time — the platform's rule for stacked
  // panels (operator, 2026-09-13); eight open tables is the wall this fold
  // exists to prevent, and two open at once is most of one.
  const { open, toggle } = useCollapsibleItem(plan.group, false);
  const writes = writeCount(plan);
  const shown = plan.items.slice(0, ROWS_SHOWN);

  return (
    <section className="rounded-lg border border-line">
      <header className="flex flex-wrap items-center gap-2 border-b border-line px-4 py-3">
        <span className="text-sm font-semibold text-ink">
          {SMARTOLT_MIGRATION_GROUP_LABELS[plan.group]}
        </span>
        <Pill state tone={GROUP_STATUS_TONE[plan.status]}>{plan.status}</Pill>
        <span className="ml-auto flex flex-wrap gap-1.5">
          {DECISIONS.map((d) =>
            plan.counts[d] > 0 ? (
              <Pill state key={d} tone={DECISION_TONE[d]}>
                {plan.counts[d]} {d}
              </Pill>
            ) : null,
          )}
          {plan.items.length === 0 && <Pill tone="neutral">nothing found</Pill>}
        </span>
      </header>

      {plan.outcome && (
        <p
          className={`border-b border-line px-4 py-2 text-xs ${
            plan.status === 'failed' ? 'text-danger' : 'text-muted'
          }`}
        >
          {plan.outcome}
          {plan.appliedAt ? ` — ${formatTimestamp(plan.appliedAt)}` : ''}
        </p>
      )}

      {(plan.counts.conflict > 0 || plan.counts.refused > 0) && (
        <p className="border-b border-line px-4 py-2 text-xs text-warn">
          {plan.counts.conflict > 0 &&
            `${plan.counts.conflict} conflict${plan.counts.conflict === 1 ? '' : 's'} — a value held here differs from SmartOLT’s; shown, never overwritten. `}
          {plan.counts.refused > 0 &&
            `${plan.counts.refused} refused — cannot be brought; each row says why.`}
        </p>
      )}

      <div className="flex flex-wrap items-center gap-3 px-4 py-2.5">
        <button
          type="button"
          onClick={toggle}
          disabled={plan.items.length === 0}
          className="flex items-center gap-1.5 text-xs font-medium text-muted hover:text-ink disabled:cursor-default disabled:opacity-60"
        >
          {open ? <ChevronDown className="size-3.5" /> : <ChevronRight className="size-3.5" />}
          {open ? 'Hide' : 'Show'} {plan.items.length} item{plan.items.length === 1 ? '' : 's'}
        </button>
        {askCount && (
          <span className="ml-auto flex items-center gap-2 text-xs text-muted">
            Type <span className="font-mono font-semibold text-ink">{writes}</span> to write{' '}
            {writes === 1 ? 'it' : 'them'}
            <Input
              value={typed}
              onChange={(e) => onType(e.target.value)}
              className="w-20 text-center font-mono"
              placeholder="0"
            />
          </span>
        )}
        {!askCount && isApplicable(plan) && writes === 0 && plan.items.length > 0 && (
          <span className="ml-auto text-xs text-faint">Nothing to write in this group.</span>
        )}
      </div>

      {open && (
        <div className="border-t border-line">
          <Table>
            <THead>
              <th className="px-4 py-2 text-left text-xs font-medium text-muted">Item</th>
              <th className="px-3 py-2 text-left text-xs font-medium text-muted">Decision</th>
              <th className="px-3 py-2 text-left text-xs font-medium text-muted">Detail</th>
              <th className="px-4 py-2 text-left text-xs font-medium text-muted">Changes</th>
            </THead>
            <TBody>
              {shown.map((item) => (
                <TRow key={item.key}>
                  <td className="px-4 py-2 align-top text-xs text-ink">{item.label}</td>
                  <td className="px-3 py-2 align-top">
                    <Pill state tone={DECISION_TONE[item.decision]}>{DECISION_LABEL[item.decision]}</Pill>
                  </td>
                  <td className="max-w-xs px-3 py-2 align-top text-xs text-muted">
                    {item.detail ?? '—'}
                  </td>
                  <td className="px-4 py-2 align-top">
                    {item.changes.length === 0 ? (
                      <span className="text-xs text-faint">—</span>
                    ) : (
                      <div className="grid gap-0.5 text-xs">
                        {item.changes.map((c) => (
                          <span key={c.field} className="text-muted">
                            <span className="font-medium text-ink">{c.field}</span>:{' '}
                            <span className="font-mono">{c.from ?? '—'}</span>
                            <span className="mx-1 text-faint">→</span>
                            <span className="font-mono text-ink">{c.to ?? '—'}</span>
                          </span>
                        ))}
                      </div>
                    )}
                  </td>
                </TRow>
              ))}
            </TBody>
          </Table>
          {plan.items.length > ROWS_SHOWN && (
            <p className="px-4 py-2 text-xs text-faint">
              …and {plan.items.length - ROWS_SHOWN} more. The counts above cover every item.
            </p>
          )}
        </div>
      )}
    </section>
  );
}
